import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import Badge from '../Badge/Badge';
import { Info } from './DoctorInfo';
import MedicationsList from './MedicationsList';
import {
  colors,
  Separator,
  ButtonArrow,
  Card2,
  ButtonArrowIcon,
  ButtonReset
} from '../../healtheraComponents';
import IconChevron from '../Icons/IconChevron';
import Notes from './Notes';
import OrderSupportNote from './OrderSupportNote';

import { formatDate } from '../../utils/formatters';
import capitalize from '../../utils/capitalize';

const NewOrder = props => {
  const [logIsVisible, setLogIsVisible] = useState(false);

  const processButtonIsPrimary = () => {
    const { processState } = props;
    return ['new', 'toApprove'].includes(processState);
  };

  const toggleLogVisibility = () => {
    setLogIsVisible(!logIsVisible);
  };

  const medicationsListIsDisabled = () => {
    const { processState } = props;
    return ['new', 'toPrepare', 'complete'].includes(processState);
  };

  const medicationsListIsTertiary = () => {
    const { processState } = props;
    return ['toPrepare', 'complete'].includes(processState);
  };

  const {
    timestamp,
    repeat: { remedies } = {},
    actions,
    comments,
    status,
    gp_status,
    toggleMedicationHandler,
    cancelActionHandler,
    dispatchedAt,
    supportNote,
    updateSupportNote,
    patient_forename,
    patient_surname,
    delivery: { option } = {},
    dependent,
    patient
  } = props;

  return (
    <>
      <OrderWrapper>
        <Inside>
          <Medications>
            <MedicationsInfo>
              {remedies && (
                <Remedies>{`${remedies.length} ${
                  remedies.length === 1 ? 'Medication' : 'Medications'
                }`}</Remedies>
              )}
              <div>{FormattedDate(timestamp * 1000)}</div>
              <Separator />
              {option && (
                <Badge
                  text={capitalize(option === 'collect' ? 'Collect' : 'Delivery')}
                  right
                  green
                />
              )}
            </MedicationsInfo>
            <MedicationsWrapper>
              <Info>{`${
                dependent
                  ? `Ordered by ${patient.forename} ${patient.surname} on behalf of`
                  : 'Ordered by'
              } ${patient_forename} ${patient_surname}`}</Info>
              <MedicationsList
                toggleMedicationHandler={toggleMedicationHandler}
                remedies={remedies}
                disabled={medicationsListIsDisabled()}
                tertiary={medicationsListIsTertiary()}
              />
            </MedicationsWrapper>
            <OrderSupportNote updateSupportNote={updateSupportNote} supportNote={supportNote} />
            {dispatchedAt && <Memos>{`Dispatched on ${formatDate(dispatchedAt)}`}</Memos>}
          </Medications>
        </Inside>
        <Notes
          name={`${patient_forename} ${patient_surname}`}
          showHeader
          notes={comments}
          showButton={false}
        />
        {
          <ButtonArrow primary={processButtonIsPrimary()}>
            <ButtonArrowIcon />
            {status === 'requested' && <span>Action Request for Approval</span>}
            {status === 'requested' && gp_status === 'delivered' && (
              <span>Move to Preperation</span>
            )}
            {status === 'processing' && gp_status === 'accepted' && (
              <span>Dispense & Complete Order</span>
            )}
          </ButtonArrow>
        }
      </OrderWrapper>
      <OrderActionLogWrapper logIsVisible={logIsVisible}>
        <OrderActionLogScroll logIsVisible={logIsVisible}>
          {actions &&
            actions.length &&
            actions.map(action => (
              <OrderActionLogRow key={action.action_id}>
                <strong>{action.action && capitalize(action.action.replace(/[_]/g, ' '))}</strong>
                <span>{FormattedDate(action.action_timestamp)}</span>
              </OrderActionLogRow>
            ))}
          {actions && actions.length === 0 && <> Actions </>}
        </OrderActionLogScroll>
      </OrderActionLogWrapper>
      <OrderActionLogActions>
        {false && (
          <Button onClick={cancelActionHandler} cancel>
            Cancel Order
          </Button>
        )}
        <Button onClick={toggleLogVisibility}>
          {logIsVisible ? 'Hide ' : 'Show '}
          Order Action Log
          <IconChevronStyled rotate={logIsVisible} />
        </Button>
      </OrderActionLogActions>
    </>
  );
};

export default NewOrder;

const FormattedDate = date => {
  let options = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  };
  date = new Date(Number(date));
  return date.toDateString() === new Date().toDateString()
    ? `Today, ${date.toLocaleTimeString('en-GB', { hour: 'numeric', minute: 'numeric' })}`
    : date.toLocaleDateString('en-GB', options);
};

export const OrderWrapper = styled(Card2)`
  width: 950px;
  display: flex;
  margin-bottom: 0;
  min-height: 400px;
  position: relative;
  z-index: 1;
`;

export const Inside = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
`;

export const MedicationsWrapper = styled.div`
  padding: 0 19px 0 25px;
`;

export const Medications = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const MedicationsInfo = styled.div`
  align-items: center;
  border-bottom: 1px solid ${colors.GRAY_LIGHT};
  display: flex;
  margin-bottom: 16px;
  padding: 19px 19px 16px 25px;
`;

export const Remedies = styled.div`
  font-weight: bold;
  margin-right: auto;
`;

export const OrderActionLogWrapper = styled.div`
  background-color: #eaeaea;
  border-radius: 0 0 19px 19px;
  margin-top: -40px;
  overflow: hidden;
  padding: ${props => (props.logIsVisible ? '64px' : '30px')} 24px 24px;
  position: relative;
  transition: padding-top 0.3s;
  will-change: padding-top;

  &:before {
    background-color: inherit;
    bottom: 0;
    content: '';
    opacity: ${props => (props.logIsVisible ? '0' : '1')};
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    transition: opacity 0.1s 0.3s;
    width: 54px;
  }
`;

export const OrderActionLogScroll = styled.div`
  max-height: ${props => (props.logIsVisible ? '185px' : '0')};
  overflow: ${props => (props.logIsVisible ? 'auto' : 'hidden')};
  padding-right: 30px;
  transition: max-height 0.3s;
  will-change: max-height;
`;

export const OrderActionLogRow = styled.div`
  display: flex;
  font-size: 15px;
  justify-content: space-between;
  padding: 15px 0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #c7c7c7;
  }

  > * {
    flex: 1;
  }
`;

export const OrderActionLogActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 25px;
  padding: 12px 25px;
`;

export const Button = styled(ButtonReset)`
  align-items: center;
  display: flex;
  font-size: 16px;
  font-weight: 600;
  margin-left: auto;

  ${props =>
    props.cancel &&
    css`
      color: ${colors.HEALTHERA_RED};
      margin-left: 0;
    `}
`;

export const IconChevronStyled = styled(IconChevron)`
  fill: ${colors.BLACK};
  height: 18px;
  margin-left: 14px;
  transition: transform 0.3s;
  transform: rotate(${props => (props.rotate ? '270deg' : '90deg')});
`;

export const Memos = styled.div`
  margin-top: auto;
  padding: 0 19px 19px 25px;
`;

export const MemosRow = styled.div`
  display: flex;
`;

export const Edit = styled(ButtonReset)`
  border-right: 3px solid;
  color: ${colors.HEALTHERA_GREEN};
  font-size: 16px;
  font-weight: 600;
  margin-left: auto;
  padding: 2px 8px;
  text-decoration: none;
`;

export const Save = styled(ButtonReset)`
  border-right: 3px solid;
  color: ${colors.HEALTHERA_GREEN};
  font-size: 16px;
  font-weight: 600;
  margin-left: auto;
  padding: 2px 8px;
  text-decoration: none;
`;

export const MemoTextarea = styled.textarea`
  border: 1px solid ${colors.GRAY_LIGHT};
  border-radius: 5px;
  box-sizing: border-box;
  font-family: inherit;
  font-size: inherit;
  max-height: 200px;
  padding: 3px 6px;
  resize: vertical;
  width: 100%;

  &:focus {
    border-color: ${colors.HEALTHERA_GREEN};
    outline: none;
  }
`;
